class SimpleSliderLogos {
  constructor(selector, options = {}) {
    this.container =
      typeof selector === 'string'
        ? document.querySelector(selector)
        : selector;
    this.options = {
      perPage: options.perPage || 6,
      perMove: options.perMove || 2,
      gap: options.gap || '2rem',
      autoplay: options.autoplay || false,
      autoplaySpeed: options.autoplaySpeed || 3000,
      loop: options.loop || true,
      pagination: options.pagination !== false,
      arrows: options.arrows !== false,
      ...options,
    };

    this.currentSlide = 0;
    this.totalSlides = 0;
    this.autoplayInterval = null;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;

    this.init();
  }

  init() {
    if (!this.container) return;

    this.track = this.container.querySelector('.splide__track');
    this.list = this.container.querySelector('.splide__list');
    this.slides = this.container.querySelectorAll('.splide__slide');
    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    this.setupStyles();
    this.setupArrows();
    this.setupPagination();
    this.setupTouch();
    this.setupKeyboard();
    this.goToSlide(0);

    window.addEventListener('resize', () => {
      this.setupTouch();
      this.updateArrows();
    });

    if (this.options.autoplay) {
      this.startAutoplay();
    }
  }

  setupStyles() {
    this.slides.forEach((slide) => {
      slide.style.removeProperty('flex');
      slide.style.removeProperty('height');
    });

    const totalSlides = this.totalSlides;
    this.list.style.width = `${totalSlides * 100}%`;
  }

  setupArrows() {
    if (!this.options.arrows) return;

    const isMobile = window.innerWidth < 1024;
    if (isMobile) return;

    const prevBtn = this.container.querySelector('.splide__arrow--prev');
    const nextBtn = this.container.querySelector('.splide__arrow--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.stopAutoplay();
        this.prev();
        setTimeout(() => this.startAutoplay(), 5000);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.stopAutoplay();
        this.next();
        setTimeout(() => this.startAutoplay(), 5000);
      });
    }

    this.updateArrows();
  }

  setupPagination() {
    if (!this.options.pagination) return;

    const pagination = this.container.querySelector('.splide__pagination');
    if (!pagination) return;

    const pages = pagination.querySelectorAll('.splide__pagination__page');
    pages.forEach((page, index) => {
      page.addEventListener('click', () => {
        this.stopAutoplay();
        this.goToSlide(index * this.options.perMove);
        setTimeout(() => this.startAutoplay(), 5000);
      });
    });

    this.updatePagination();
  }

  setupTouch() {
    const isMobile = window.innerWidth < 1024;
    if (!isMobile || !this.track) return;

    this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e));
    this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e));
    this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e));

    this.track.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.track.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.track.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    this.track.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
  }

  handleTouchStart(e) {
    this.startX = e.touches[0].clientX;
    this.isDragging = true;
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    this.currentX = e.touches[0].clientX;
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;

    const diff = this.startX - this.currentX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    this.isDragging = false;
  }

  handleMouseDown(e) {
    this.startX = e.clientX;
    this.isDragging = true;
    this.track.style.cursor = 'grabbing';
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    this.currentX = e.clientX;
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;

    const diff = this.startX - this.currentX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    this.isDragging = false;
    this.track.style.cursor = 'grab';
  }

  handleMouseLeave(e) {
    this.isDragging = false;
    this.track.style.cursor = 'grab';
  }

  setupKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
      } else if (e.key === 'ArrowRight') {
        this.next();
      }
    });
  }

  updateArrows() {
    if (!this.options.arrows) return;

    const isMobile = window.innerWidth < 1024;
    if (isMobile) return;

    const prevBtn = this.container.querySelector('.splide__arrow--prev');
    const nextBtn = this.container.querySelector('.splide__arrow--next');

    if (prevBtn) {
      prevBtn.style.opacity = this.currentSlide === 0 ? '0.5' : '1';
      prevBtn.style.pointerEvents = this.currentSlide === 0 ? 'none' : 'auto';
    }

    if (nextBtn) {
      const maxSlide = this.totalSlides - this.options.perPage;
      nextBtn.style.opacity = this.currentSlide >= maxSlide ? '0.5' : '1';
      nextBtn.style.pointerEvents =
        this.currentSlide >= maxSlide ? 'none' : 'auto';
    }
  }

  updatePagination() {
    if (!this.options.pagination) return;

    const pagination = this.container.querySelector('.splide__pagination');
    if (!pagination) return;

    const pages = pagination.querySelectorAll('.splide__pagination__page');
    const currentPage = Math.floor(this.currentSlide / this.options.perMove);

    pages.forEach((page, index) => {
      page.classList.toggle('is-active', index === currentPage);
    });
  }

  startAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }

    this.autoplayInterval = setInterval(() => {
      if (this.currentSlide >= this.totalSlides - this.options.perPage) {
        this.goToSlide(0);
      } else {
        this.next();
      }
    }, this.options.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  goToSlide(index) {
    if (index < 0) {
      index = this.options.loop ? this.totalSlides - this.options.perPage : 0;
    } else if (index >= this.totalSlides - this.options.perPage + 1) {
      index = this.options.loop ? 0 : this.totalSlides - this.options.perPage;
    }

    this.currentSlide = index;

    const isMobile = window.innerWidth < 1024;
    const slideWidth = isMobile ? 100 : 150;
    const gap = isMobile ? 16 : 32;

    const translateX = -(index * (slideWidth + gap));

    this.list.style.transform = `translateX(${translateX}px)`;

    this.updateActiveSlides();
    this.updateArrows();
    this.updatePagination();
  }

  next() {
    this.goToSlide(this.currentSlide + this.options.perMove);
  }

  prev() {
    this.goToSlide(this.currentSlide - this.options.perMove);
  }

  updateActiveSlides() {
    this.slides.forEach((slide, i) => {
      slide.classList.remove('active');
    });

    const startIndex = this.currentSlide;
    const endIndex = Math.min(
      startIndex + this.options.perPage - 1,
      this.totalSlides - 1,
    );

    for (let i = startIndex; i <= endIndex; i++) {
      if (this.slides[i]) {
        this.slides[i].classList.add('active');
      }
    }
  }

  destroy() {
    this.stopAutoplay();
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const logoSliders = document.querySelectorAll('.splide-logos');

  logoSliders.forEach((slider, index) => {
    new SimpleSliderLogos(slider, {
      perPage: window.innerWidth < 1024 ? 4 : 6,
      perMove: window.innerWidth < 1024 ? 1 : 2,
      gap: '1rem',
      autoplay: false,
      autoplaySpeed: 3000,
      loop: true,
      pagination: false,
      arrows: true,
    });
  });
});
